import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import subscriptionService from '../../services/subscription-service';
import productService from '../../db/product-service';
import './SubscriptionPlans.scss';

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasSubscriptions, setHasSubscriptions] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [shots, setShots] = useState([]);
  const [smoothies, setSmoothies] = useState([]);
  const [premiumProducts, setPremiumProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [deliverySchedule, setDeliverySchedule] = useState({});
  const [showDateModal, setShowDateModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productType, setProductType] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = sessionStorage.getItem('isUserAuthenticated') === 'true';
  const customerEmail = sessionStorage.getItem('customerEmail');
  const customerId = sessionStorage.getItem('customerId');

  useEffect(() => {
    loadPlansAndProducts();
    if (isAuthenticated && customerId) {
      checkUserSubscriptions();
    }

    const handleRefreshSubscriptions = () => {
      if (isAuthenticated && customerId) {
        checkUserSubscriptions();
      }
    };

    window.addEventListener('refreshSubscriptions', handleRefreshSubscriptions);
    return () => {
      window.removeEventListener('refreshSubscriptions', handleRefreshSubscriptions);
    };
  }, [isAuthenticated, customerId, location.key]);



  const loadPlansAndProducts = async () => {
    try {
      const [plansData, productsData] = await Promise.all([
        subscriptionService.getSubscriptionPlans(),
        productService.getAllProducts()
      ]);
      setPlans(plansData);
      
      const planOrder = ['complete_wellness', 'smoothie_fuel', 'immunity_booster'];
      const sorted = [...plansData].sort((a, b) => {
        const ai = planOrder.indexOf(a.planType);
        const bi = planOrder.indexOf(b.planType);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });
      setPlans(sorted);
      
      if (productsData && Array.isArray(productsData)) {
        const shotProducts = productsData.filter(p => {
          const categories = Array.isArray(p.category) ? p.category : (p.category ? [p.category] : []);
          return !categories.includes('premium') && 
                 (categories.includes('shots') || p.name.toLowerCase().includes('juice') || p.name.toLowerCase().includes('shot'));
        });
        const smoothieProducts = productsData.filter(p => {
          const categories = Array.isArray(p.category) ? p.category : (p.category ? [p.category] : []);
          return !categories.includes('premium') && 
                 (categories.includes('smoothies') || p.name.toLowerCase().includes('smoothie'));
        });
        const premiumItems = productsData.filter(p => {
          const categories = Array.isArray(p.category) ? p.category : (p.category ? [p.category] : []);
          return categories.includes('premium');
        });
        
        setShots(shotProducts);
        setSmoothies(smoothieProducts);
        setPremiumProducts(premiumItems);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const checkUserSubscriptions = async () => {
    if (customerId && isAuthenticated) {
      try {
        const userHasSubscriptions = await subscriptionService.hasUserSubscriptions(customerId);
        setHasSubscriptions(userHasSubscriptions);
      } catch (error) {
        console.error('Error checking subscriptions:', error);
        setHasSubscriptions(false);
      }
    }
  };

  const selectPlan = (plan) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setSelectedPlan(plan);
    setSelectedProducts([]);
    setDeliverySchedule(generateDeliveryDates());
    setTimeout(() => {
      document.getElementById('product-selection')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const generateDeliveryDates = () => {
    const schedule = {};
    const startDate = new Date();
    startDate.setDate(startDate.getDate() + 1);
    let deliveryCount = 0;
    
    while (deliveryCount < 20) {
      const day = startDate.getDay();
      if (day >= 1 && day <= 5) {
        const dateKey = startDate.toISOString().split('T')[0];
        schedule[dateKey] = { shot: null, smoothie: null, premium: null, status: 'pending' };
        deliveryCount++;
      }
      startDate.setDate(startDate.getDate() + 1);
    }
    return schedule;
  };

  const openDateSelector = (product, type) => {
    setSelectedProduct(product);
    setProductType(type);
    setShowDateModal(true);
  };

  const toggleDateSelection = (dateKey) => {
    const newSchedule = { ...deliverySchedule };
    const typeKey = productType === 'shot' ? 'shot' : productType === 'smoothie' ? 'smoothie' : 'premium';
    
    if (newSchedule[dateKey][typeKey] === selectedProduct._id) {
      newSchedule[dateKey][typeKey] = null;
    } else {
      const currentCount = getProductSelectionCount(selectedProduct._id);
      const maxCount = selectedPlan?.planType === 'complete_wellness' ? 20 : 
        selectedPlan?.planType === 'premium' ? selectedPlan?.premiumPerMonth :
        (productType === 'shot' ? selectedPlan?.shotsPerMonth : selectedPlan?.smoothiesPerMonth);
      
      if (currentCount >= maxCount) {
        alert(`Maximum ${maxCount} selections reached for this product`);
        return;
      }
      newSchedule[dateKey][typeKey] = selectedProduct._id;
    }
    
    setDeliverySchedule(newSchedule);
    updateSelectedProducts(newSchedule);
  };

  const selectAllDates = () => {
    const newSchedule = { ...deliverySchedule };
    const typeKey = productType === 'shot' ? 'shot' : productType === 'smoothie' ? 'smoothie' : 'premium';
    const maxCount = selectedPlan?.planType === 'complete_wellness' ? 20 : 
      selectedPlan?.planType === 'premium' ? selectedPlan?.premiumPerMonth :
      (productType === 'shot' ? selectedPlan?.shotsPerMonth : selectedPlan?.smoothiesPerMonth);
    
    let count = 0;
    Object.keys(newSchedule).forEach(dateKey => {
      if (count < maxCount && !newSchedule[dateKey][typeKey]) {
        newSchedule[dateKey][typeKey] = selectedProduct._id;
        count++;
      }
    });
    
    setDeliverySchedule(newSchedule);
    updateSelectedProducts(newSchedule);
  };

  const deselectAllDates = () => {
    const newSchedule = { ...deliverySchedule };
    const typeKey = productType === 'shot' ? 'shot' : productType === 'smoothie' ? 'smoothie' : 'premium';
    
    Object.keys(newSchedule).forEach(dateKey => {
      if (newSchedule[dateKey][typeKey] === selectedProduct._id) {
        newSchedule[dateKey][typeKey] = null;
      }
    });
    
    setDeliverySchedule(newSchedule);
    updateSelectedProducts(newSchedule);
  };

  const selectColumn = (columnIndex) => {
    const newSchedule = { ...deliverySchedule };
    const typeKey = productType === 'shot' ? 'shot' : productType === 'smoothie' ? 'smoothie' : 'premium';
    const dateKeys = Object.keys(deliverySchedule);
    
    for (let i = columnIndex; i < dateKeys.length; i += 5) {
      if (!newSchedule[dateKeys[i]][typeKey]) {
        newSchedule[dateKeys[i]][typeKey] = selectedProduct._id;
      }
    }
    
    setDeliverySchedule(newSchedule);
    updateSelectedProducts(newSchedule);
  };

  const deselectColumn = (columnIndex) => {
    const newSchedule = { ...deliverySchedule };
    const typeKey = productType === 'shot' ? 'shot' : productType === 'smoothie' ? 'smoothie' : 'premium';
    const dateKeys = Object.keys(deliverySchedule);
    
    for (let i = columnIndex; i < dateKeys.length; i += 5) {
      if (newSchedule[dateKeys[i]][typeKey] === selectedProduct._id) {
        newSchedule[dateKeys[i]][typeKey] = null;
      }
    }
    
    setDeliverySchedule(newSchedule);
    updateSelectedProducts(newSchedule);
  };

  const updateSelectedProducts = (schedule) => {
    const productCounts = {};
    Object.values(schedule).forEach(day => {
      if (day.shot) productCounts[day.shot] = (productCounts[day.shot] || 0) + 1;
      if (day.smoothie) productCounts[day.smoothie] = (productCounts[day.smoothie] || 0) + 1;
      if (day.premium) productCounts[day.premium] = (productCounts[day.premium] || 0) + 1;
    });
    
    const products = Object.entries(productCounts).map(([productId, quantity]) => ({
      productId,
      quantity
    }));
    setSelectedProducts(products);
  };

  const getProductSelectionCount = (productId) => {
    return Object.values(deliverySchedule).filter(day => 
      day.shot === productId || day.smoothie === productId || day.premium === productId
    ).length;
  };

  const isProductDisabled = (productId) => {
    const count = getProductSelectionCount(productId);
    const maxCount = selectedPlan?.planType === 'complete_wellness' ? 20 : 
      (productType === 'shot' ? selectedPlan?.shotsPerMonth : selectedPlan?.smoothiesPerMonth);
    return count >= maxCount;
  };

  const isDateSelected = (dateKey) => {
    if (!selectedProduct) return false;
    const typeKey = productType === 'shot' ? 'shot' : productType === 'smoothie' ? 'smoothie' : 'premium';
    return deliverySchedule[dateKey]?.[typeKey] === selectedProduct._id;
  };

  const isDateDisabled = (dateKey) => {
    if (!selectedProduct) return false;
    const typeKey = productType === 'shot' ? 'shot' : productType === 'smoothie' ? 'smoothie' : 'premium';
    const assignedProduct = deliverySchedule[dateKey]?.[typeKey];
    return assignedProduct && assignedProduct !== selectedProduct._id;
  };



  const getTotalPremium = () => {
    return selectedProducts
      .filter(p => premiumProducts.some(pr => pr._id === p.productId))
      .reduce((sum, p) => sum + p.quantity, 0);
  };

  const getTotalShots = () => {
    return selectedProducts
      .filter(p => shots.some(s => s._id === p.productId))
      .reduce((sum, p) => sum + p.quantity, 0);
  };

  const getTotalSmoothies = () => {
    return selectedProducts
      .filter(p => smoothies.some(s => s._id === p.productId))
      .reduce((sum, p) => sum + p.quantity, 0);
  };

  const handleSubscribe = () => {
    if (!selectedPlan) return;

    const totalShots = getTotalShots();
    const totalSmoothies = getTotalSmoothies();
    const totalPremium = getTotalPremium();

    // Validation for Premium plan
    if (selectedPlan.planType === 'premium') {
      if (totalPremium !== selectedPlan.premiumPerMonth) {
        alert(`Please select exactly ${selectedPlan.premiumPerMonth} premium products`);
        return;
      }
    }
    // Special validation for Complete Wellness Bundle
    else if (selectedPlan.planType === 'complete_wellness') {
      if (totalShots + totalSmoothies !== 40) {
        alert('Complete Wellness Bundle requires exactly 40 items total (max 20 shots + max 20 smoothies)');
        return;
      }
      if (totalShots > 20) {
        alert('Complete Wellness Bundle allows maximum 20 shots');
        return;
      }
      if (totalSmoothies > 20) {
        alert('Complete Wellness Bundle allows maximum 20 smoothies');
        return;
      }
    } else {
      if (selectedPlan.shotsPerMonth > 0 && totalShots !== selectedPlan.shotsPerMonth) {
        alert(`Please select exactly ${selectedPlan.shotsPerMonth} immunity shots`);
        return;
      }

      if (selectedPlan.smoothiesPerMonth > 0 && totalSmoothies !== selectedPlan.smoothiesPerMonth) {
        alert(`Please select exactly ${selectedPlan.smoothiesPerMonth} smoothies`);
        return;
      }
    }

    if (selectedProducts.length === 0) {
      alert('Please select at least one product');
      return;
    }

    navigate('/subscription/checkout', {
      state: { planType: selectedPlan.planType, selectedProducts, deliverySchedule }
    });
  };

  if (loading) return <div className="loading">Loading subscription plans...</div>;

  return (
    <div className="subscription-plans">
      <div className="header">
        <h1>Choose Your Wellness Subscription</h1>
        <p>Fresh, organic nutrition delivered Monday-Friday</p>
        {isAuthenticated && (
          <div className="header-buttons">
            <button 
              className="my-subscriptions-btn"
              onClick={() => {
                navigate('/customer');
                setTimeout(() => {
                  const event = new CustomEvent('setCustomerTab', { detail: 'subscriptions' });
                  window.dispatchEvent(event);
                }, 100);
              }}
            >
              My Subscriptions
            </button>
            <button 
              className="refresh-btn"
              onClick={() => {
                console.log('Manual refresh triggered');
                checkUserSubscriptions().then(() => {
                  checkTrialEligibility();
                });
              }}
              style={{ marginLeft: '10px', padding: '8px 16px', fontSize: '14px' }}
            >
              🔄 Refresh
            </button>
          </div>
        )}
      </div>

      <div className="plans-grid">
        {plans.filter(plan => plan.planType !== 'premium' && plan.planType !== 'free_trial').map(plan => (
          <div key={plan.planType} className={`plan-card ${plan.planType} ${selectedPlan?.planType === plan.planType ? 'selected' : ''} ${plan.featured && !plan.contactOnly ? 'featured' : ''}`}>
            {plan.featured && !plan.contactOnly && plan.offerSettings?.showOffer && (
              <div className="offer-badge">
                🎁 {plan.offerSettings.offerName}
              </div>
            )}
            <div className="plan-header">
              <h3>{plan.name}</h3>
              <div className="price">
                {plan.originalPrice && plan.offerSettings?.showOffer && (
                  <div className="original-price-strike">
                    <span className="strike">₹{plan.originalPrice}</span>
                    <span className="save-badge">Save ₹{plan.originalPrice - plan.monthlyPrice}</span>
                  </div>
                )}
                <div className="plan-price">₹{plan.monthlyPrice}<span>/month</span></div>
                <div className="delivery-options">
                  <div className="delivery-option">
                    <span className="option-label">Store Pickup:</span>
                    <span className="option-price">&#8377;{plan.monthlyPrice} total</span>
                  </div>
                  <div className="delivery-option">
                    <span className="option-label">Home Delivery:</span>
                    <span className="option-price">&#8377;{plan.monthlyPrice + 500} total</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="plan-details">
              <p className="description">{plan.description}</p>

              {plan.contactOnly ? (
                <div className="features">
                  <div className="feature"><span className="count">40</span><span>Servings / Month</span></div>
                  <div className="feature"><span className="count">2</span><span>Servings Per Day</span></div>
                  <div className="feature"><span>Monday – Friday</span></div>
                  <div style={{ marginTop: '12px', padding: '10px 12px', backgroundColor: '#f3e5f5', borderLeft: '4px solid #7b1fa2', borderRadius: '4px', fontSize: '13px', color: '#4a148c' }}>
                    <strong>🌿 Tailored for any health goal or condition</strong><br/>
                    Diabetes • BP • Gut Health • Immunity • Weight • Skin • Energy • and more
                  </div>
                </div>
              ) : (
                <div className="features">
                  {plan.shotsPerMonth > 0 && (
                    <div className="feature">
                      <span className="count">{plan.shotsPerMonth}</span>
                      <span>Immunity Shots</span>
                    </div>
                  )}
                  {plan.smoothiesPerMonth > 0 && (
                    <div className="feature">
                      <span className="count">{plan.smoothiesPerMonth}</span>
                      <span>Functional Smoothies</span>
                    </div>
                  )}
                  <div className="feature"><span className="count">20</span><span>Delivery Days</span></div>
                  <div className="feature"><span>Monday – Friday</span></div>
                </div>
              )}
            </div>

            {plan.contactOnly ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <a
                  href="https://wa.me/919390649371?text=Hi%2C%20I%20am%20interested%20in%20the%20Custom%20Wellness%20Bundle.%20Please%20help%20me%20customise%20my%20plan."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="select-btn"
                  style={{ textAlign: 'center', textDecoration: 'none', backgroundColor: '#25D366', display: 'block' }}
                >
                  💬 Contact on WhatsApp
                </a>
                <a
                  href="tel:+919390649371"
                  className="select-btn"
                  style={{ textAlign: 'center', textDecoration: 'none', backgroundColor: '#1976d2', display: 'block' }}
                >
                  📞 Call Us: +91 93906 49371
                </a>
              </div>
            ) : (
              <button
                className="select-btn"
                onClick={() => selectPlan(plan)}
              >
                {selectedPlan?.planType === plan.planType ? 'Selected ✓' : (isAuthenticated ? 'Select Plan' : 'Login to Subscribe')}
              </button>
            )}
          </div>
        ))}
      </div>

      {selectedPlan && (
        <div id="product-selection" className="product-selection-section">
          <h2>Customize Your {selectedPlan.name}</h2>
          <p className="selection-instruction">Select your preferred products for your subscription</p>

          <div className="product-selection">
            {selectedPlan.planType === 'premium' ? (
              <div className="product-category">
                <h3>Premium Products ({getTotalPremium()} / {selectedPlan.premiumPerMonth})</h3>
                <div className="products-grid">
                  {premiumProducts.map(product => {
                    const count = getProductSelectionCount(product._id);
                    return (
                      <div key={product._id} className="product-card" onClick={() => openDateSelector(product, 'premium')}>
                        {product.image_url && <img src={product.image_url} alt={product.name} />}
                        <h4>{product.name}</h4>
                        <div className="selection-count">{count} selected</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <>
                {selectedPlan.shotsPerMonth > 0 && (
              <div className="product-category">
                <h3>Immunity Shots ({getTotalShots()} / {selectedPlan.planType === 'complete_wellness' ? '20 max' : selectedPlan.shotsPerMonth})</h3>
                <div className="products-grid">
                  {shots.map(product => {
                    const count = getProductSelectionCount(product._id);
                    return (
                      <div key={product._id} className="product-card" onClick={() => openDateSelector(product, 'shot')}>
                        {product.image_url && <img src={product.image_url} alt={product.name} />}
                        <h4>{product.name}</h4>
                        <div className="selection-count">{count} selected</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {selectedPlan.smoothiesPerMonth > 0 && (
              <div className="product-category">
                <h3>Smoothies ({getTotalSmoothies()} / {selectedPlan.planType === 'complete_wellness' ? '20 max' : selectedPlan.smoothiesPerMonth})</h3>
                <div className="products-grid">
                  {smoothies.map(product => {
                    const count = getProductSelectionCount(product._id);
                    return (
                      <div key={product._id} className="product-card" onClick={() => openDateSelector(product, 'smoothie')}>
                        {product.image_url && <img src={product.image_url} alt={product.name} />}
                        <h4>{product.name}</h4>
                        <div className="selection-count">{count} selected</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
              </>
            )}
          </div>

          {showDateModal && (
            <div className="date-modal-overlay" onClick={() => setShowDateModal(false)}>
              <div className="date-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Select Dates for {selectedProduct?.name} <span className="selected-count">(Selected: {getProductSelectionCount(selectedProduct?._id)})</span></h3>
                  <div className="header-actions">
                    <div className="select-all-checkbox">
                      <label>
                        <input 
                          type="checkbox" 
                          onChange={(e) => {
                            if (e.target.checked) {
                              selectAllDates();
                            } else {
                              deselectAllDates();
                            }
                          }}
                        />
                        <span>Select All</span>
                      </label>
                    </div>
                    <button className="close-btn" onClick={() => setShowDateModal(false)}>×</button>
                  </div>
                </div>
                <div className="column-checkboxes">
                  {[1, 2, 3, 4, 5].map(col => (
                    <label key={col}>
                      <input 
                        type="checkbox" 
                        onChange={(e) => {
                          if (e.target.checked) {
                            selectColumn(col - 1);
                          } else {
                            deselectColumn(col - 1);
                          }
                        }}
                      />
                      <span>Col {col}</span>
                    </label>
                  ))}
                </div>
                <div className="dates-grid">
                  {Object.entries(deliverySchedule).map(([dateKey, day]) => {
                    const date = new Date(dateKey);
                    const dayName = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                    const selected = isDateSelected(dateKey);
                    const disabled = isDateDisabled(dateKey);
                    
                    return (
                      <div
                        key={dateKey}
                        className={`date-option ${selected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
                        onClick={() => !disabled && toggleDateSelection(dateKey)}
                      >
                        <div className="date-label">{dayName}</div>
                        {selected && <span className="check">✓</span>}
                      </div>
                    );
                  })}
                </div>
                <button className="done-btn" onClick={() => setShowDateModal(false)}>Done</button>
              </div>
            </div>
          )}

          <div className="subscribe-section">
            <button className="subscribe-btn" onClick={handleSubscribe}>
              Subscribe Now
            </button>
          </div>
        </div>
      )}

      <div className="benefits">
        <h3>Why Subscribe?</h3>
        <div className="benefits-grid">
          <div className="benefit">
            <h4>Consistent Nutrition</h4>
            <p>Never miss your daily wellness routine</p>
          </div>
          <div className="benefit">
            <h4>Better Pricing</h4>
            <p>Save compared to individual orders</p>
          </div>
          <div className="benefit">
            <h4>Flexible Delivery</h4>
            <p>Monday-Friday delivery to your doorstep</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans;